/** Helpers para envolver las APIs basadas en eventos de IndexedDB en Promises.
 *  Dos patrones distintos: por request (lecturas) y por transacción (escrituras). */

/** Convierte un `IDBRequest` en Promise (lecturas): resuelve con `req.result`
 *  en `onsuccess` o rechaza con `req.error` en `onerror`. */
export function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Convierte una `IDBTransaction` en Promise (escrituras): resuelve en
 *  `oncomplete` (cuando todas las operaciones de la transacción terminaron) o
 *  rechaza con `tx.error` en `onerror`. */
export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Tabla de una base local: nombre, campo clave y, si tiene, índices. Cada
 *  índice se llama igual que el campo que indexa. */
export type TablaBaseLocal = {
  /** Nombre de la tabla (object store). */
  nombre: string;
  /** Campo clave de la tabla (`keyPath`). */
  clave: string;
  /** Campos indexados; el índice lleva el mismo nombre que el campo. */
  indices?: readonly string[];
};

/** Opciones de una base local. */
export type OpcionesBaseLocal = {
  /** Si la base guardada en el navegador tiene una versión menor que esta, al
   *  abrirla se vacía entera y se vuelve a llenar desde el servidor. Sirve
   *  cuando un cambio de versión cambia la forma de los datos guardados. Sin
   *  esta opción, un cambio de versión conserva todos los datos. */
  vaciarAntesDe?: number;
};

/** Conexión perezosa a una base local, compartida por todas las llamadas. */
export type BaseLocal = {
  /** Abre la base la primera vez y después reusa la conexión. Si la apertura
   *  falla, la próxima llamada vuelve a intentar. */
  abrir(): Promise<IDBDatabase>;
};

/** Crea la conexión a una base local de la app (decidido 2026-09-30). Al
 *  cambiar de versión crea las tablas e índices que faltan, quita los que ya
 *  no se declaran y conserva los datos y el avance de sincronización. Solo
 *  vacía la base entera si la versión guardada es menor que
 *  `opciones.vaciarAntesDe`, o si alguna tabla cambió de campo clave: en esos
 *  casos los datos viejos no sirven y se vuelven a bajar del servidor.
 *
 *  Nunca se borra una base al cerrar sesión ni al cambiar de cuenta: los datos
 *  de cada cliente conviven, separados por finca o por cliente (decisión del
 *  usuario, 2026-09-30).
 *
 *  Reemplaza al `onupgradeneeded` que cada base escribía a mano: en 14 de
 *  ellas creaba las tablas sin mirar si ya existían, y un navegador que traía
 *  la versión 1 no podía abrir la versión 2. */
export function fx_crearBaseLocal(
  nombre: string,
  version: number,
  tablas: readonly TablaBaseLocal[],
  opciones: OpcionesBaseLocal = {},
): BaseLocal {
  let conexion: Promise<IDBDatabase> | null = null;
  return {
    abrir() {
      conexion ??= abrirBaseLocal(nombre, version, tablas, opciones).catch(
        (error: unknown) => {
          conexion = null;
          throw error;
        },
      );
      return conexion;
    },
  };
}

/** Abre la base y, si cambió la versión, ajusta sus tablas (ver
 *  `fx_crearBaseLocal`). */
function abrirBaseLocal(
  nombre: string,
  version: number,
  tablas: readonly TablaBaseLocal[],
  opciones: OpcionesBaseLocal,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(nombre, version);
    request.onupgradeneeded = (evento) => {
      const db = request.result;
      const transaccion = request.transaction;
      if (!transaccion) return;

      const vaciarPorVersion =
        evento.oldVersion > 0 &&
        opciones.vaciarAntesDe !== undefined &&
        evento.oldVersion < opciones.vaciarAntesDe;
      const cambioDeClave = tablas.some(
        (tabla) =>
          db.objectStoreNames.contains(tabla.nombre) &&
          transaccion.objectStore(tabla.nombre).keyPath !== tabla.clave,
      );
      const vaciar = vaciarPorVersion || cambioDeClave;

      const declaradas = new Set(tablas.map((tabla) => tabla.nombre));
      for (const existente of Array.from(db.objectStoreNames)) {
        if (vaciar || !declaradas.has(existente)) {
          db.deleteObjectStore(existente);
        }
      }
      for (const tabla of tablas) {
        prepararTabla(db, transaccion, tabla);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Crea la tabla si falta y deja sus índices tal como están declarados. */
function prepararTabla(
  db: IDBDatabase,
  transaccion: IDBTransaction,
  tabla: TablaBaseLocal,
): void {
  const store = db.objectStoreNames.contains(tabla.nombre)
    ? transaccion.objectStore(tabla.nombre)
    : db.createObjectStore(tabla.nombre, { keyPath: tabla.clave });

  const indices = tabla.indices ?? [];
  for (const existente of Array.from(store.indexNames)) {
    if (!indices.includes(existente) || store.index(existente).keyPath !== existente) {
      store.deleteIndex(existente);
    }
  }
  for (const campo of indices) {
    if (!store.indexNames.contains(campo)) {
      store.createIndex(campo, campo);
    }
  }
}
